import { useEffect, useMemo } from 'react';
import { create } from '@bufbuild/protobuf';
import { App, DatePicker, Form, Input, Modal, Radio, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { applicationApi } from '@/api/application.api';
import { channelApi } from '@/api/channel.api';
import { agentTokenApi } from '@/api/agentToken.api';
import { ApiError } from '@/api/errors';
import { useQuery } from '@tanstack/react-query';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  CheckAgentTokenNameRequestSchema,
  CreateAgentTokenRequestSchema,
  type CreateAgentTokenData,
} from '@/proto/agentplatform/httpv2/token_pb';
import { ListChannelAccountsRequestSchema } from '@/proto/agentplatform/httpv2/channel_pb';
import { TokenType } from '@/proto/agentplatform/v2/token_pb';
import { ChannelType } from '@/proto/agentplatform/v1/channel_pb';
import { localDateToExpireNano } from '@/utils/utils';
import { useCreateAgentToken } from '../hooks/useAgentTokens';

type ExpireMode = 'forever' | 'custom';

interface Props {
  open: boolean;
  tokenType: TokenType;
  onClose: () => void;
  /** 创建成功后回调，调用方需立即弹出 TokenRevealDialog 展示明文。 */
  onCreated: (data: CreateAgentTokenData) => void;
  /** 锁定绑定的 WebSocket 渠道账号（仅 WEBSOCKET 生效）：预选且禁止修改。
   *  用于从渠道账号详情页新建 Token 时固定当前账号。 */
  lockedChannelAccountId?: string;
}

interface FormValues {
  name: string;
  boundResourceId: string;
  expireMode: ExpireMode;
  expireDate?: Dayjs;
}

export default function CreateTokenDialog({
  open,
  tokenType,
  onClose,
  onCreated,
  lockedChannelAccountId,
}: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const expireMode = Form.useWatch('expireMode', form);
  const boundResourceId = Form.useWatch('boundResourceId', form);

  const createMut = useCreateAgentToken(tokenType);

  const { data: appsRes } = useQuery({
    queryKey: ['applications', 'list', 'token-binding'],
    queryFn: () => applicationApi.list(create(ListRequestSchema, { page: 1, pageSize: 50 })),
    enabled: open,
  });
  const { data: chRes } = useQuery({
    queryKey: ['channel-accounts', 'list', 'token-binding', ChannelType.BRIDGE],
    queryFn: () =>
      channelApi.listChannelAccounts(
        create(ListChannelAccountsRequestSchema, {
          page: 1,
          pageSize: 50,
          type: ChannelType.BRIDGE,
        })
      ),
    enabled: open && tokenType === TokenType.WEBSOCKET,
  });

  const apps = useMemo(() => appsRes?.data ?? [], [appsRes]);
  const wsAccounts = useMemo(() => chRes?.items ?? [], [chRes]);

  useEffect(() => {
    if (open) {
      const defaultWs = lockedChannelAccountId || wsAccounts[0]?.id || '';
      form.setFieldsValue({
        name: '',
        boundResourceId: tokenType === TokenType.API ? apps[0]?.id || '' : defaultWs,
        expireMode: 'forever',
        expireDate: undefined,
      });
    }
  }, [open, tokenType, apps, wsAccounts, lockedChannelAccountId, form]);

  // 绑定对象变化会影响名称唯一性范围，需重新校验名称。
  useEffect(() => {
    if (form.isFieldTouched('name')) {
      form.validateFields(['name']).catch(() => {});
    }
  }, [boundResourceId, form]);

  // 名称唯一性预检：API→同一 agent 实例；WS→同一渠道账号。
  // 校验接口异常不阻塞创建（create 路径仍会强制校验），仅在「明确占用」时报错。
  const validateName = async (_: unknown, value: string) => {
    if (!value || !boundResourceId) return;
    let available: boolean;
    try {
      available = await agentTokenApi.checkName(
        create(CheckAgentTokenNameRequestSchema, {
          type: tokenType,
          name: value,
          agentInstanceId: tokenType === TokenType.API ? boundResourceId : '',
          channelAccountId: tokenType === TokenType.WEBSOCKET ? boundResourceId : '',
        })
      );
    } catch {
      return;
    }
    if (!available) throw new Error('该名称已存在');
  };

  async function onOk() {
    const v = await form.validateFields();
    const expireAt =
      v.expireMode === 'custom' && v.expireDate
        ? localDateToExpireNano(v.expireDate.format('YYYY-MM-DD'))
        : 0n;
    try {
      const res = await createMut.mutateAsync(
        create(CreateAgentTokenRequestSchema, {
          type: tokenType,
          name: v.name,
          agentInstanceId: tokenType === TokenType.API ? v.boundResourceId : '',
          channelAccountId: tokenType === TokenType.WEBSOCKET ? v.boundResourceId : '',
          expireAt,
        })
      );
      onClose();
      onCreated(res);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  const title = `新建 ${tokenType === TokenType.API ? 'Agent API' : 'WebSocket'} Token`;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText="创建 Token"
      cancelText="取消"
      confirmLoading={createMut.isPending}
      width={500}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Token 名称"
          name="name"
          validateTrigger={['onBlur', 'onSubmit']}
          rules={[{ required: true, message: 'Token 名称必填' }, { validator: validateName }]}
        >
          <Input placeholder={tokenType === TokenType.API ? '例如 prod-bff' : '例如 h5-frontend'} />
        </Form.Item>

        {tokenType === TokenType.API ? (
          <Form.Item
            label="绑定 Agent 实例"
            name="boundResourceId"
            rules={[{ required: true, message: '请选择绑定对象' }]}
            help="Token 只能调用绑定的实例"
          >
            <Select options={apps.map((a) => ({ value: a.id, label: `${a.name}（${a.id}）` }))} />
          </Form.Item>
        ) : (
          <Form.Item
            label="绑定 WebSocket 渠道账号"
            name="boundResourceId"
            rules={[{ required: true, message: '请选择 WebSocket 渠道账号' }]}
          >
            <Select
              disabled={!!lockedChannelAccountId}
              options={wsAccounts.map((w) => ({
                value: w.id,
                label: `${w.name}（${w.id}）`,
              }))}
            />
          </Form.Item>
        )}

        <Form.Item label="有效期" name="expireMode">
          <Radio.Group>
            <Radio value="forever">永久有效</Radio>
            <Radio value="custom">自定义到期日</Radio>
          </Radio.Group>
        </Form.Item>

        {expireMode === 'custom' && (
          <Form.Item
            name="expireDate"
            rules={[{ required: true, message: '请选择到期日' }]}
            extra="到期当天 23:59:59 自动停用"
          >
            <DatePicker
              placeholder="选择到期日"
              disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
